import { ChangeDetectorRef, Component, inject, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatFormField, MatLabel, MatOption, MatSelect } from '@angular/material/select'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { ToastService } from '../toast.service'
import { Call, CallResourceType, CallsService } from '../calls.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { DocumentUploadInfo, toSourceDocumentFormat } from '../utils'
import { FileUploadType, getUploadErrorMessage } from '../uploads.service'

export type UserUploadedResource = {
  type: CallResourceType
  extensions: string[]
  maxSize: number | null
  uploadType: FileUploadType
}

export const MAX_AGENDA_SIZE_IN_BYTES = 3_000_000
export const MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES = 25_000_000

export const callResourceTypesToUpload: { [type: string]: UserUploadedResource } = {
  AGENDA: { type: 'AGENDA', extensions: ['pdf', 'docx'], maxSize: MAX_AGENDA_SIZE_IN_BYTES, uploadType: 'agenda' },
}

export const resourcesAcceptedFromUser = Object.values(callResourceTypesToUpload)

export function getMissingDocumentsForCall(call: Call) {
  return resourcesAcceptedFromUser.filter(
    (document) => document.type === 'CUSTOM_DOCUMENT' || !call.userUploadedResources.map((resource) => resource.resourceType).includes(document.type)
  )
}

@Component({
  selector: 'app-call-document-upload',
  standalone: true,
  imports: [MatSelect, TranslatePipe, TranslateDirective, MatOption, FormsModule, MatButton, MatLabel, MatFormField, SpinnerComponent, FileUploadComponent],
  templateUrl: './call-document-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-document-upload.component.scss',
})
export class CallDocumentUploadComponent {
  @Input({ required: true })
  callId!: number

  @Input({ required: true })
  acceptedDocuments!: UserUploadedResource[]

  @ViewChild(FileUploadComponent)
  fileUploadComponent!: FileUploadComponent

  callsService = inject(CallsService)
  toastService = inject(ToastService)
  changeDetectorRef = inject(ChangeDetectorRef)
  selectedDocumentType: string | null = null
  acceptedExtensions: string[] = []
  maxSize: number | null = null
  importAgendaMutation = this.callsService.importAgenda()
  uploading = false
  uploadInfo: DocumentUploadInfo | null = null

  onDocumentTypeChange() {
    const document = this.acceptedDocuments.find((doc) => doc.type === this.selectedDocumentType) ?? null
    this.acceptedExtensions = document !== null ? document.extensions : []
    this.maxSize = document !== null ? document.maxSize : null
    if (this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.removeFile()
    }
  }

  async onUploadClick() {
    this.uploading = true
    try {
      await this.importAgendaMutation.mutateAsync({
        callId: this.callId,
        data: {
          targetResourceExtension: this.uploadInfo!.extension,
          uploadKey: this.uploadInfo!.key,
          fileName: this.uploadInfo!.fileName,
          sourceDocumentFormat: toSourceDocumentFormat(this.uploadInfo!.extension),
        },
      })
    } catch (error) {
      console.error('Failed to upload document:', error)
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.UPLOAD_FAILED')
      }
    } finally {
      this.uploading = false
      this.uploadInfo = null
      this.selectedDocumentType = null
      this.changeDetectorRef.detectChanges()
    }
  }

  getUploadType(selectedDocumentType: string) {
    return callResourceTypesToUpload[selectedDocumentType].uploadType
  }
}
